import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import draftmanJobApi from '../../../APIs/draftman';
import pricingApi from '../../../APIs/pricing';
import { ReactComponent as CloseSVG } from '../../../assets/icons/cross.svg';
import ConfirmationModalJob from '../../../components/ConfirmationModal/ConfirmationModal';
import {
  BrowseFile,
  Button,
  ErrorMessage,
  FormContainer,
  Input,
  TextArea,
  Typography,
} from '../../../designComponents';
import ListImages from '../../../designComponents/BrowseFile/ListImage';
import { RootState } from '../../../store/selectors';
import {
  FileWithKeys,
  getFileURL,
  requestAPI,
  uploadFilesToS3,
} from '../../../utils';
import { PlansCard } from './PlansCard';
import { CloseAdditionalWrapperStyled, HireProfessionalStyled } from './style';

// const MAX_NUMBER_OF_DRAFT = 4;
let initialValues: InitialValuesTypes = {
  shippingAddress: '',
  jobTitle: '',
  drafts: [],
  description: [''],
  pricingPlan: '',
};

type InitialValuesTypes = {
  shippingAddress: string;
  jobTitle: string;
  drafts: { url?: string; _id?: string; draftFile?: File }[];
  description: string[];
  pricingPlan: string;
};

const validationSchema = yup.object({
  shippingAddress: yup.string().required('Shipping Address is required.'),
  jobTitle: yup.string().required('Job Title is required.'),
  drafts: yup.array().nullable().min(1, 'At least 1 draft is required.'),
  description: yup
    .array()
    .of(yup.string().required('Description is required'))
    .required('At least one description is required'),
});

export const updateDraftmanJobValidation = (hasDraftUrl: boolean) => {
  return yup.object({
    shippingAddress: yup.string().required('Shipping Address is required.'),
    jobTitle: yup.string().required('Job Title is required.'),
    drafts: hasDraftUrl
      ? yup.array().nullable().min(0, 'At least 1 draft is required.')
      : yup.array().nullable().min(1, 'At least 1 draft is required.'),
    description: yup
      .array()
      .of(yup.string().required('Description is required'))
      .required('At least one description is required'),
  });
};

const AddDraftmanJob = () => {
  const [seeAll, setSeeAll] = useState<number[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const pricingPlansData = useSelector(
    (state: RootState) => state.pricingPlanTypeData.data,
  );

  const [searchParams] = useSearchParams();
  const editString = searchParams.get('edit');
  const editDraftmanJobId = editString ? editString.split('-')[1] : '';

  const singleJob = useSelector(
    (state: RootState) => state.singleDraftmanJobData,
  );
  const getSingleJob = async () => {
    try {
      await requestAPI(
        draftmanJobApi.getSingleJobOfDraftman(editDraftmanJobId || ''),
      );
    } catch (error: any) {
      console.error(error);
      toast.error(error);
    }
  };

  useEffect(() => {
    if (editDraftmanJobId) {
      getSingleJob();
    }
  }, [editDraftmanJobId]);

  const [pricingPlan, setPricingPlan] = useState(
    editDraftmanJobId ? singleJob?.pricingPlan?._id || '' : '',
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  if (singleJob && singleJob?._id === editDraftmanJobId) {
    initialValues = {
      description: singleJob.notes || [],
      drafts: singleJob.drafts || [],
      jobTitle: singleJob?.title || '',
      pricingPlan: singleJob?.pricingPlan?._id || '',
      shippingAddress: singleJob?.shippingAddress || '',
    };
  }

  const getPricingPlansData = async () => {
    try {
      await requestAPI(pricingApi.getPricingPlanType('DRAFTS_JOB'));
    } catch (error: any) {
      console.error(error);
      toast.error(error);
    }
  };

  useEffect(() => {
    getPricingPlansData();
  }, []);

  const formikRef = useRef<FormikProps<InitialValuesTypes>>(null);

  const handleSeeAll = (id: number) => {
    setSeeAll([...seeAll, id]);
  };

  const handleRemoveAdditionalRequirement = (idx: number) => {
    if (formikRef && formikRef.current) {
      const additionalReq = [...formikRef.current.values.description];

      additionalReq.splice(idx, 1);

      formikRef.current.setFieldValue('description', additionalReq);
    }
  };
  const handleAddAdditionalRequirement = () => {
    if (formikRef && formikRef.current) {
      const additionalDescription = formikRef.current.values.description || [];
      const newDescription = '';
      formikRef.current.setFieldValue('description', [
        ...additionalDescription,
        newDescription,
      ]);
    }
  };

  const handleCreateDraftmanJob = async (values: InitialValuesTypes) => {
    try {
      if (files.length === 0) return;

      setLoading(true);
      let fileWithKeys: FileWithKeys[] = [];
      if (files && files.length > 0) {
        fileWithKeys = (await uploadFilesToS3(files)) as FileWithKeys[];

        if (fileWithKeys.length > 0) {
          await requestAPI(
            draftmanJobApi.createDraftmanJob({
              drafts: files.map((val: any) => ({
                url: getFileURL(fileWithKeys, val),
              })),
              notes: values.description,
              pricingPlan: pricingPlan,
              shippingAddress: values.shippingAddress,
              title: values.jobTitle,
            }),
          );
        }
      }
      setLoading(false);
      toast.success('Draftmanjob created successfully');
      navigate('/create-jobs');
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      toast.error(error);
    } finally {
      setIsOpen(false);
    }
  };
  const handleUpadteDraftmanJob = async (values: InitialValuesTypes) => {
    try {
      setLoading(true);

      const draftFiles = values.drafts
        .map((item) => item.draftFile)
        .filter(Boolean) as File[];
      let fileWithKeys: FileWithKeys[] = (await uploadFilesToS3(
        draftFiles,
      )) as FileWithKeys[];

      if (fileWithKeys) {
        await requestAPI(
          draftmanJobApi.updateDraftmanJob(editDraftmanJobId, {
            drafts: values?.drafts
              ? values.drafts.map((val) => ({
                url: val?.draftFile
                  ? getFileURL(fileWithKeys, val?.draftFile)
                  : val?.url,
              }))
              : [],
            notes: values.description,
            pricingPlan: pricingPlan,
            shippingAddress: values.shippingAddress,
            title: values.jobTitle,
          }),
        );
        setLoading(false);
        toast.success('Draftmanjob updated successfully');
        navigate(-1);
      } else {
        await requestAPI(
          draftmanJobApi.updateDraftmanJob(editDraftmanJobId, {
            drafts:
              singleJob?.drafts?.map((item) => ({
                url: item.url,
              })) || [],
            notes: values.description,
            pricingPlan: pricingPlan,
            shippingAddress: values.shippingAddress,
            title: values.jobTitle,
          }),
        );
        setLoading(false);
        toast.success('Draftmanjob updated successfully');
        navigate(-1);
      }

      navigate('/create-jobs');
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      toast.error(error);
    } finally {
      setIsOpen(false);
    }
  };
  return (
    <HireProfessionalStyled>
      <Typography variant="title" className="heading">
        Hire Professional
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          marginBottom: '20px',
        }}
      >
        <Typography variant="title">Choose a Package</Typography>
        <Typography>
          We offer different packages tailored to meet various needs.
        </Typography>
        <PlansCard
          onClick={handleSeeAll}
          seeAll={seeAll}
          setSeeAll={setSeeAll}
          setPricePlan={setPricingPlan}
          plansData={pricingPlansData}
          singlePricePlan={pricingPlan}
          jobId={editDraftmanJobId}
        />

        <ErrorMessage text={!pricingPlan ? 'Pricing plan is required' : ''} />
      </div>
      <Formik
        onSubmit={
          editDraftmanJobId ? handleUpadteDraftmanJob : handleCreateDraftmanJob
        }
        initialValues={initialValues}
        innerRef={formikRef}
        validationSchema={
          editDraftmanJobId
            ? updateDraftmanJobValidation(
              typeof formikRef.current?.values?.drafts !== 'undefined' &&
                  formikRef.current?.values?.drafts.length > 0,
            )
            : validationSchema
        }
        enableReinitialize
      >
        {({ values, setFieldValue, touched, errors }) => {
          return (
            <Form>
              <FormContainer
                label="Job Title"
                error={touched.jobTitle ? errors.jobTitle : ''}
              >
                <Input
                  placeholder="Job Title"
                  value={values.jobTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    setFieldValue('jobTitle', value);
                  }}
                />
              </FormContainer>
              <FormContainer
                label="Shipping Address"
                error={touched.shippingAddress ? errors.shippingAddress : ''}
              >
                <Input
                  placeholder="Add shipping address"
                  value={values.shippingAddress}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    setFieldValue('shippingAddress', value);
                  }}
                />
              </FormContainer>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <Typography strong>Initial Drafts</Typography>
                <Typography>
                  Please upload the initial drafts of your building. Our
                  professionals will use them as references to create accurate
                  building drawings.
                </Typography>
                <Typography style={{ marginBottom: '20px' }}>
                  You can upload drafts in either SVG, PNG, or JPG format, and
                  you also have the option to add multiple drafts.
                </Typography>
              </div>
              <FormContainer
                label="Draft"
                error={
                  touched.drafts &&
                  values.drafts.length === 0 &&
                  typeof errors.drafts === 'string'
                    ? errors.drafts
                    : ''
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flexWrap: 'wrap',
                  }}
                >
                  {Array.isArray(values.drafts)
                    ? values.drafts.map((draft, index) => {
                      const file = draft?.draftFile;
                      if (!file) {
                        return (
                            <ListImages
                              imagesURL={[
                                {
                                  id: `${draft._id}`,
                                  URL: draft?.url || '',
                                },
                              ]}
                              enableRemoveImage
                              onRemove={(id: string) => {
                                setFieldValue(
                                  'drafts',
                                  values?.drafts?.filter((item) => {
                                    return String(item._id) !== id;
                                  }),
                                );
                              }}
                              key={index}
                            />
                        );
                      }

                      if (!file) {
                        return;
                      }

                      return (
                          <div key={index}>
                            <ListImages
                              files={[file]}
                              setFiles={setFiles}
                              noWrapper
                              onRemoveFile={() => {
                                values.drafts.splice(index, 1);
                                if (setFiles) {
                                  setFiles([...files]);
                                }
                              }}
                            />
                          </div>
                      );
                    })
                    : ''}

                  <BrowseFile
                    setFiles={(browsedFiles: File[]) => {
                      setFiles(browsedFiles);

                      const newBlueprints = [...values.drafts];

                      newBlueprints.push({
                        draftFile: browsedFiles[browsedFiles.length - 1],
                      });
                      setFieldValue('drafts', newBlueprints);
                    }}
                    files={files}
                    accepts=".jpg, .png, .jpeg, .svg"
                    showSelectFiles={false}
                  />
                </div>
              </FormContainer>
              <Typography className="text">Description</Typography>

              {values.description.map((desc, index) => {
                return (
                  <div key={index} style={{ maxWidth: '560px' }}>
                    {index !== 0 && (
                      <CloseAdditionalWrapperStyled>
                        <CloseSVG
                          onClick={() =>
                            handleRemoveAdditionalRequirement(index)
                          }
                        />
                      </CloseAdditionalWrapperStyled>
                    )}

                    <FormContainer
                      label=""
                      error={
                        touched.description && errors.description
                          ? errors.description[index]
                          : ''
                      }
                    >
                      <TextArea
                        key={index}
                        placeholder="Please add description..."
                        value={desc || ''}
                        onChange={(
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          const { value } = e.target;
                          const updatedValues = [...values.description];
                          updatedValues[index] = value;

                          setFieldValue('description', updatedValues);
                        }}
                        showCount
                        maxLength={180}
                      />
                    </FormContainer>
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                <Button
                  variant="gray"
                  type="button"
                  onClick={handleAddAdditionalRequirement}
                >
                  Add More Description
                </Button>

                <Button
                  variant="secondary"
                  onClick={async () => {
                    if (formikRef.current) {
                      await formikRef.current.validateForm();
                      formikRef.current.setTouched({
                        drafts: [{ url: true }],
                        description: true,
                        jobTitle: true,
                        pricingPlan: true,
                        shippingAddress: true,
                      });
                      if (Object.keys(formikRef.current.errors).length > 0) {
                        return;
                      }
                      setIsOpen(true);
                    }
                  }}
                >
                  {editDraftmanJobId ? 'Update Job' : 'Create job'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
      {isOpen ? (
        <ConfirmationModalJob
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          message={`Are you sure want to ${editDraftmanJobId ? 'update' : 'create'} this job?`}
          title="Draftman Job"
          okText={editDraftmanJobId ? 'Update' : 'Create'}
          onOk={async () => {
            if (formikRef.current) {
              await formikRef.current.validateForm();

              if (Object.keys(formikRef.current.errors).length > 0) {
                setIsOpen(false);
              }
              formikRef.current.handleSubmit();
            }
          }}
          loading={loading}
        />
      ) : (
        ''
      )}
    </HireProfessionalStyled>
  );
};

export default AddDraftmanJob;
